/**
 * @fileoverview Catches render-time errors that no router loader produced.
 * @module lib/errors/ErrorBoundary
 */

import { Component, type ErrorInfo, type ReactNode } from "react"

import { AppError } from "./AppError"
import { logError } from "./logger"

/** Props accepted by {@link ErrorBoundary}. */
export interface ErrorBoundaryProps {
	/** Subtree to protect. */
	readonly children: ReactNode
	/** Rendered instead of the subtree once an error is caught. */
	readonly fallback: (error: AppError, reset: () => void) => ReactNode
}

/** Internal state of {@link ErrorBoundary}. */
interface ErrorBoundaryState {
	readonly error: AppError | null
}

/**
 * React error boundary wired to the central error sink.
 *
 * @description
 * React only surfaces render/lifecycle errors through a class boundary, so
 * this stays a class component. Everything it catches is normalised and
 * reported exactly like an error caught anywhere else.
 *
 * @example
 * <ErrorBoundary fallback={(error, reset) => <ErrorTemplate error={error} onRetry={reset} />}>
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props)
		this.state = { error: null }
	}

	/**
	 * Moves the boundary into its error state.
	 *
	 * @param error - The thrown value.
	 * @returns The next state.
	 */
	static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
		return { error: logError(error, "boundary") }
	}

	/**
	 * Reports the React component stack alongside the error.
	 *
	 * @param error - The thrown value.
	 * @param info - React's component stack.
	 */
	override componentDidCatch(error: unknown, info: ErrorInfo): void {
		logError(
			new AppError("Render failed", {
				cause: error,
				context: { componentStack: info.componentStack },
			}),
			"boundary"
		)
	}

	/**
	 * Clears the error state so the subtree can render again.
	 *
	 * Declared as an arrow field: the fallback receives it detached from the
	 * instance, so it must carry its own `this`.
	 */
	reset = (): void => {
		this.setState({ error: null })
	}

	override render(): ReactNode {
		const { error } = this.state
		if (error !== null) {
			return this.props.fallback(error, this.reset)
		}
		return this.props.children
	}
}
